import { gql, useQuery } from "@apollo/client";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Tabs, Tab, Box, styled, Collapse } from "@mui/material";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { StringParam, useQueryParams } from "use-query-params";

import {
  CampaignContactTag,
  GetActiveCampaignContactForTexterQuery,
  GetActiveCampaignContactForTexterQueryVariables,
  MarkAssignmentNeedsResponseMutation,
  MarkAssignmentNeedsResponseMutationVariables,
  MarkAssignmentNotNeedsResponseMutation,
  MarkAssignmentNotNeedsResponseMutationVariables,
  MarkAssignmentOptedOutMutation,
  MarkAssignmentOptedOutMutationVariables,
  SendMessageToAssignmentMutation,
  SendMessageToAssignmentMutationVariables,
  SetAssignmentContactNoteMutation,
  SetAssignmentContactNoteMutationVariables,
  UpdateAssignmentContactTagsMutation,
  UpdateAssignmentContactTagsMutationVariables,
  EscalateAssignmentMutation,
  EscalateAssignmentMutationVariables,
  DeescalateAssignmentMutation,
  DeescalateAssignmentMutationVariables,
  InboxType,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import NeedsResponseBullet from "~frontend/components/threads/NeedsResponseBullet";
import Buttons from "~frontend/components/threads/ThreadControls/Buttons";
import ResponseScripts from "~frontend/components/threads/ThreadControls/ResponseScripts";
import SendMessageBox, {
  SendMessageBoxHandle,
} from "~frontend/components/threads/ThreadControls/SendMessageBox";
import Tags from "~frontend/components/threads/ThreadControls/Tags";
import UnsavedChanges from "~frontend/components/threads/ThreadControls/UnsavedChanges";
import {
  Message,
  TagAppliedBy,
  UnsavedTag,
  updateUnsavedTagsForNewResponseScriptTags,
} from "~frontend/components/threads/ThreadControls/types";
import {
  DEESCALATE_ASSIGNMENT,
  ESCALATE_ASSIGNMENT,
  GET_ASSIGNMENTS,
  LIST_ASSIGNMENTS,
  MARK_ASSIGNMENT_NEEDS_RESPONSE,
  MARK_ASSIGNMENT_NOT_NEEDS_RESPONSE,
  MARK_ASSIGNMENT_OPTED_OUT,
  SEND_MESSAGE_TO_ASSIGNMENT,
  SET_ASSIGNMENT_CONTACT_NOTE,
  UPDATE_ASSIGNMENT_CONTACT_TAGS,
} from "~frontend/graphql/assignments";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { useTexterSettings } from "~frontend/providers/TexterSettingsProvider";
import { palette } from "~frontend/theme";
import { formatContactData } from "~frontend/util/contactFields";
import { graphqlErrorHasCode } from "~frontend/util/errors";
import { userIsSubaccountAdminOrHigher } from "~frontend/util/roles";
import useMousetrap from "~frontend/util/useMousetrap";
import useMutation from "~frontend/util/useMutation";

import { GET_ACTIVE_CAMPAIGN_CONTACT_FOR_TEXTER } from "../../../graphql/campaignContacts";
import AssignmentContactDetails from "./AssignmentContactDetails";
import { Assignment } from "./types";

const StyledTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    backgroundColor: palette.navy,
    top: 0,
    height: "4px",
  },
});

const StyledTab = styled(Tab)({
  margin: "4px 2px 0",
  backgroundColor: palette.lightGray,
  color: palette.darkGray,
  flexDirection: "row",

  "&.Mui-selected": {
    backgroundColor: palette.lightestGray,
    color: palette.navy,
  },
});

const EMPTY_MESSAGE: Message = { text: "", attachments: [] };

export function AssignmentControls({
  assignment,
  inboxType,
  inboxName,
  refetchAssignment,
  scrollToLatest,
  markAssignmentLoading,
  markAssignmentLoaded,
  advanceToNextAssignment,
}: {
  assignment: Assignment;
  inboxType: InboxType;
  inboxName: string | null;
  refetchAssignment: () => Promise<any>;
  scrollToLatest: () => void;
  markAssignmentLoading: () => void;
  markAssignmentLoaded: () => void;
  advanceToNextAssignment?: () => void;
}): ReactElement {
  const { autoadvanceEnabled } = useTexterSettings();
  const snackbar = useSnackbar();
  const intl = useIntl();
  const [_queryParams, setQueryParams] = useQueryParams({
    assignment: StringParam,
  });

  const confirmTagRemoval = useConfirm();
  const { membership } = useSubaccount()!;

  const [tabsOpen, setTabsOpen] = useState(false);

  // We store the unsaved state at this level of the component tree, rather
  // than in the individual tabs. This lets us centrally managed the "unsaved"
  // warning when you try to navigate away, and also lets us auto-save notes
  // and tags when you send a message and reset your state when you change
  // the context campaign.
  const [unsavedContactNote, setUnsavedContactNote] = useState<string | null>(
    null
  );
  const [mutationPending, setMutationPending] = useState(false);
  const [unsavedTags, setUnsavedTags] = useState<UnsavedTag[]>([]);
  const [removedSavedTagIds, setRemovedSavedTagIds] = useState<string[]>([]);

  const sendMessageBoxRef = useRef<SendMessageBoxHandle>(null);

  useEffect(() => {
    // When the assignment changes, reset the unsaved state
    setUnsavedContactNote(null);
    setUnsavedTags([]);
    setRemovedSavedTagIds([]);
    sendMessageBoxRef.current?.setMessage(EMPTY_MESSAGE);
  }, [
    assignment.id,
    sendMessageBoxRef,
    setUnsavedContactNote,
    setUnsavedTags,
    setRemovedSavedTagIds,
  ]);

  // fetch data: we fetch the contact and the campaign
  // separately (rather than querying for the campaign field
  // on the contact) to take advantage of Apollo caching: we don't
  // want to re-fetch all the script, etc. for a campaign over and
  // over as we navigate between its contacts.
  const contactData = useQuery<
    GetActiveCampaignContactForTexterQuery,
    GetActiveCampaignContactForTexterQueryVariables
  >(GET_ACTIVE_CAMPAIGN_CONTACT_FOR_TEXTER, {
    variables: {
      input: {
        campaignContactId: assignment.campaignContact!.id!,
      },
    },
    skip: !assignment.campaignContact,
  });

  const [sendMessageToAssignment] = useMutation<
    SendMessageToAssignmentMutation,
    SendMessageToAssignmentMutationVariables
  >(SEND_MESSAGE_TO_ASSIGNMENT, {
    update(cache, { data }) {
      if (!data) {
        return;
      }
      const assignment = data.sendMessageToAssignment;
      cache.writeFragment({
        id: `AssignmentSummary:${assignment.id}`,
        fragment: gql`
          fragment AssignmentSummaryFromAssignment on AssignmentSummary {
            needsTexterResponse
            lastMessageAt
            expiresAt
          }
        `,
        data: {
          needsTexterResponse: assignment.needsTexterResponse,
          lastMessageAt: assignment.lastMessageAt,
          expiresAt: assignment.expiresAt,
        },
      });
    },
    refetchQueries: [
      GET_ACTIVE_CAMPAIGN_CONTACT_FOR_TEXTER,
      LIST_ASSIGNMENTS,
      GET_ASSIGNMENTS,
    ],
  });

  function wrapMutation<A extends any[], R>(
    fn: (...args: A) => Promise<R>
  ): (...args: A) => Promise<R> {
    return async (...args) => {
      setMutationPending(true);
      markAssignmentLoading();
      try {
        return await fn(...args);
      } finally {
        setMutationPending(false);
        markAssignmentLoaded();
      }
    };
  }

  const [escalateAssignment] = useMutation<
    EscalateAssignmentMutation,
    EscalateAssignmentMutationVariables
  >(ESCALATE_ASSIGNMENT, {
    refetchQueries: [LIST_ASSIGNMENTS],
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to escalate assignment",
      description: "Error message when we are unable to escalate an assignment",
    }),
  });
  const [deescalateAssignment] = useMutation<
    DeescalateAssignmentMutation,
    DeescalateAssignmentMutationVariables
  >(DEESCALATE_ASSIGNMENT, {
    refetchQueries: [LIST_ASSIGNMENTS],
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to de-escalate assignment",
      description:
        "Error message when we are unable to deescalate an assignment",
    }),
  });

  const [markAssignmentOptedOut] = useMutation<
    MarkAssignmentOptedOutMutation,
    MarkAssignmentOptedOutMutationVariables
  >(MARK_ASSIGNMENT_OPTED_OUT, {
    variables: {
      input: { assignmentId: assignment.id },
    },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to Opt Out contact",
      description: "Error message when we fail to opt-out a contact",
    }),
    refetchQueries: [LIST_ASSIGNMENTS],
    awaitRefetchQueries: true,
  });

  const [markAssignmentNeedsResponse] = useMutation<
    MarkAssignmentNeedsResponseMutation,
    MarkAssignmentNeedsResponseMutationVariables
  >(MARK_ASSIGNMENT_NEEDS_RESPONSE, {
    variables: { input: { assignmentId: assignment.id } },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to mark thread as needing a response",
      description:
        "Error message when we are unable to mark a thread as needing a response",
    }),
    refetchQueries: [LIST_ASSIGNMENTS],
    awaitRefetchQueries: true,
  });

  const [markAssignmentNotNeedsResponse] = useMutation<
    MarkAssignmentNotNeedsResponseMutation,
    MarkAssignmentNotNeedsResponseMutationVariables
  >(MARK_ASSIGNMENT_NOT_NEEDS_RESPONSE, {
    variables: { input: { assignmentId: assignment.id } },
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to mark thread as not needing a response",
      description:
        "Error message when we are unable to mark a thread as not needing a response",
    }),
    refetchQueries: [LIST_ASSIGNMENTS],
    awaitRefetchQueries: true,
  });

  const [updateAssignmentContactTags] = useMutation<
    UpdateAssignmentContactTagsMutation,
    UpdateAssignmentContactTagsMutationVariables
  >(UPDATE_ASSIGNMENT_CONTACT_TAGS, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to update contact tags",
      description: "Error message when we fail to update tags",
    }),
    refetchQueries: [
      GET_ACTIVE_CAMPAIGN_CONTACT_FOR_TEXTER,
      LIST_ASSIGNMENTS,
      GET_ASSIGNMENTS,
    ],
  });

  const [setAssignmentContactNote] = useMutation<
    SetAssignmentContactNoteMutation,
    SetAssignmentContactNoteMutationVariables
  >(SET_ASSIGNMENT_CONTACT_NOTE, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to save contact note",
      description: "Error message when we fail to save a contact note",
    }),
    refetchQueries: [GET_ACTIVE_CAMPAIGN_CONTACT_FOR_TEXTER],
    awaitRefetchQueries: true,
  });

  function wrapConfirmTagRemoval<T, U extends unknown[]>(
    f: (...args: U) => Promise<T>
  ): (...args: U) => Promise<T | undefined> {
    return async (...args) => {
      const removedVanTags = contactData.data?.getCampaignContact.tags.filter(
        t =>
          removedSavedTagIds.includes(t.tag.id) &&
          t.tag.integrationActions.length > 0
      );

      if (
        userIsSubaccountAdminOrHigher(membership) &&
        removedVanTags &&
        removedVanTags.length > 0
      ) {
        const { confirmed } = await confirmTagRemoval({
          title: "",
          description:
            "Note: deleting a tag will only remove it from Scale to Win's data, and will not remove the mapped VAN action from NGP VAN.",
        });
        if (!confirmed) {
          return;
        }
      }

      return f(...args);
    };
  }

  // We wrap all of the mutations: this allows us to alter state when
  // these mutations are called (e.g. auto-saving the note and tags when
  // you send a message; clearing the message when you send it successfully).
  const handleSaveNote = wrapMutation(async () => {
    if (unsavedContactNote == null) {
      throw new Error("Must have an unsaved contact note");
    }

    const result = await setAssignmentContactNote({
      variables: {
        input: { assignmentId: assignment.id, text: unsavedContactNote },
      },
    });
    if (!result.errors) {
      setUnsavedContactNote(null);
    }

    return result;
  });
  const handleSaveTags = wrapConfirmTagRemoval(
    wrapMutation(async () => {
      const result = await updateAssignmentContactTags({
        variables: {
          input: {
            assignmentId: assignment.id,
            applyTagIds: unsavedTags.map(t => t.tagId),
            removeTagIds: removedSavedTagIds,
          },
        },
      });
      if (!result.errors) {
        setUnsavedTags([]);
        setRemovedSavedTagIds([]);
      }
      return result;
    })
  );

  const handleSendMessage = wrapConfirmTagRemoval(
    wrapMutation(async message => {
      const latestMessageId = _.sortBy(assignment.messages, [
        m => -m.threadOrderTime,
        m => m.id,
      ])[0].id;

      const sendMessageResult = await sendMessageToAssignment({
        variables: {
          input: {
            assignmentId: assignment.id,
            latestMessageId,
            messageText: message.text,
            mediaAttachments: message.attachments,
            applyTagIds: unsavedTags.map(t => t.tagId),
            removeTagIds: removedSavedTagIds,
            updateContactNote: unsavedContactNote,
          },
        },
      });

      if (!sendMessageResult.errors) {
        if (
          unsavedTags.length > 0 ||
          unsavedContactNote != null ||
          removedSavedTagIds.length > 0
        ) {
          setUnsavedContactNote(null);
          setUnsavedTags([]);
          setRemovedSavedTagIds([]);
          // If we saved tags or notes, refetch the updated data for the contact
          // (but don't block the rest of the UI -- just the contact details pane
          // will stay loading until this refetch is done).
          contactData
            .refetch()
            .catch(e => console.error("Error refetching contact", e));
        }

        scrollToLatest();

        if (autoadvanceEnabled && advanceToNextAssignment) {
          advanceToNextAssignment();
        }

        return sendMessageResult;
      }

      // There were errors: refresh the thread (to get any new message that may
      // have caused a conflict), and the contact (to see if the note/tags were
      // saved even if the message failed).
      //
      // Note that we don't attempt to see whether the message was sent: that's
      // the last thing the mutation does (so it's the least likely to fail), and
      // there's no perfect way to check whether the message was sent (e.g. if
      // the same user sent the same message from another tab, we'd still see
      // that message as sent even if this particular mutation failed). We just
      // scroll to the bottom of the message list and let the user decide what to
      // do next based on the current state.
      await Promise.all([
        refetchAssignment().catch(e =>
          console.error("Error refetching thread", e)
        ),
        (async () => {
          const newContact = await contactData.refetch();
          if (newContact.data) {
            // check if the note was saved
            if (
              unsavedContactNote != null &&
              newContact.data.getCampaignContact.note?.text ===
                unsavedContactNote
            ) {
              setUnsavedContactNote(null);
            }

            // check if the tags were saved
            const newTagIds = new Set(
              newContact.data.getCampaignContact.tags.map(
                contactTag => contactTag.tag.id
              )
            );
            const stillUnsavedTags = unsavedTags.filter(
              t => !newTagIds.has(t.tagId)
            );
            setUnsavedTags(stillUnsavedTags);

            const notRemovedTags = removedSavedTagIds.filter(tid =>
              newTagIds.has(tid)
            );
            setRemovedSavedTagIds(notRemovedTags);
          }
        })().catch(e => console.error("Error refetching contact", e)),
      ]);

      if (
        graphqlErrorHasCode("WRONG_LATEST_MESSAGE_ID", sendMessageResult.errors)
      ) {
        snackbar.enqueueSnackbar({
          variant: "warning",
          message: intl.formatMessage({
            defaultMessage:
              "Another message was sent or received in this thread. Please review the latest messages and try again.",
            description:
              "Error message when a message fails to send because another message was sent or received in the thread",
          }),
        });
      } else if (
        graphqlErrorHasCode("CONTACT_OPTED_OUT", sendMessageResult.errors)
      ) {
        snackbar.enqueueSnackbar({
          variant: "error",
          message: intl.formatMessage({
            defaultMessage:
              "Failed to send message: contact has been opted out",
            description:
              "Error message when a message fails to send because the contact was opted out",
          }),
        });
      } else {
        snackbar.enqueueSnackbar({
          variant: "error",
          message: intl.formatMessage({
            defaultMessage: "Failed to send message",
            description: "Error message when we fail to send a message",
          }),
        });
      }

      scrollToLatest();
      return sendMessageResult;
    })
  );

  const handleOptOut = wrapMutation(async () => {
    // clear message
    sendMessageBoxRef.current?.setMessage(EMPTY_MESSAGE);
    await markAssignmentOptedOut();
    setQueryParams({ assignment: null });
    if (advanceToNextAssignment) {
      advanceToNextAssignment();
    }
  });

  const handleMarkNeedsResponse = assignment.campaignContact
    ? wrapMutation(markAssignmentNeedsResponse)
    : null;

  const handleMarkNotNeedsResponse = assignment.campaignContact
    ? wrapMutation(async () => {
        const { errors } = await markAssignmentNotNeedsResponse();
        if (!errors) {
          if (autoadvanceEnabled && advanceToNextAssignment) {
            advanceToNextAssignment();
          }
        }
      })
    : null;

  const handleEscalate = wrapMutation(async (inboxId: string) => {
    return escalateAssignment({
      variables: {
        input: {
          escalationInboxId: inboxId,
          assignmentId: assignment.id,
        },
      },
    });
  });

  const handleDeescalate = wrapMutation(async () => {
    return deescalateAssignment({
      variables: {
        input: {
          assignmentId: assignment.id,
        },
      },
    });
  });

  const TABS = ["responses", "tags", "contactDetails", "expand"];
  type Tab = (typeof TABS)[number];

  const [activeTab, setActiveTab] = useState<Tab>("responses");

  function handleSelectTab(tab: Tab) {
    if (tab === "expand") {
      setTabsOpen(!tabsOpen);
    } else {
      setTabsOpen(true);
      setActiveTab(tab);
    }
  }

  useMousetrap("shift+space", evt => {
    // prevent the current element from handling the spacebar
    evt.preventDefault();
    document.body.blur();

    if (!tabsOpen) {
      // open first tab
      handleSelectTab(TABS[0]);
      return;
    }

    const newIdx = TABS.indexOf(activeTab) + 1;
    if (newIdx >= TABS.length || TABS[newIdx] === "expand") {
      // close tabs
      setTabsOpen(false);
      sendMessageBoxRef.current?.focusMessageInput();
    } else {
      handleSelectTab(TABS[newIdx]);
    }
  });

  function renderActiveTab() {
    if (contactData.error) {
      return <ErrorCard errors={[contactData.error]} />;
    }

    if (contactData.loading) {
      return <Loading />;
    }

    const parsedContactData =
      contactData.data?.getCampaignContact &&
      formatContactData({
        contactData: JSON.parse(
          contactData.data!.getCampaignContact.texterViewableData ?? "{}"
        ),
        campaignContactFields: assignment.campaign!.contactFields,
      });

    switch (activeTab) {
      case "responses":
        return (
          <ResponseScripts
            contactData={parsedContactData!}
            campaign={assignment.campaign!}
            isOptedOut={false}
            setMessage={(msg, tagAndGroupIds) => {
              if (!mutationPending) {
                sendMessageBoxRef.current?.setMessage(msg);

                setUnsavedTags(oldUnsavedTags =>
                  updateUnsavedTagsForNewResponseScriptTags(
                    tagAndGroupIds,
                    oldUnsavedTags,
                    contactData.data!.getCampaignContact
                  )
                );
              }
            }}
          />
        );
      case "tags":
        return (
          <Tags
            contactTags={
              contactData.data?.getCampaignContact.tags as CampaignContactTag[]
            }
            campaign={assignment.campaign!}
            unsavedTags={unsavedTags}
            removedSavedTagIds={removedSavedTagIds}
            addUnsavedTag={(tagId, tagGroupId) => {
              setUnsavedTags([
                ...unsavedTags.filter(t => t.tagId !== tagId),
                { tagId, tagGroupId, appliedBy: TagAppliedBy.Manual },
              ]);
            }}
            removeUnsavedTag={tagId =>
              setUnsavedTags(unsavedTags.filter(t => t.tagId !== tagId))
            }
            removeSavedTag={tagId => {
              setRemovedSavedTagIds([...removedSavedTagIds, tagId]);
            }}
            cancelRemoveSavedTag={tagId => {
              setRemovedSavedTagIds(
                removedSavedTagIds.filter(t => t !== tagId)
              );
            }}
            mutationPending={mutationPending}
            onSaveTags={handleSaveTags}
          />
        );
      case "contactDetails":
        return (
          <AssignmentContactDetails
            assignment={assignment}
            campaign={assignment.campaign!}
            contact={contactData.data!.getCampaignContact}
            handleSaveNote={handleSaveNote}
            mutationPending={mutationPending}
            onChangeUnsavedContactNote={setUnsavedContactNote}
            unsavedContactNote={unsavedContactNote}
          />
        );
    }
  }

  return (
    <>
      <UnsavedChanges
        unsavedMessage={() =>
          sendMessageBoxRef.current?.hasUnsavedChanges() ?? false
        }
        unsavedNote={unsavedContactNote != null}
        unsavedTags={unsavedTags.length > 0 || removedSavedTagIds.length > 0}
        setActiveTab={handleSelectTab}
      />
      <Box sx={{ backgroundColor: palette.lightGray, padding: "24px 40px" }}>
        <SendMessageBox
          ref={sendMessageBoxRef}
          onSubmit={handleSendMessage}
          isOptedOut={false}
          mutationPending={mutationPending}
          textingHoursStart={
            assignment.campaign?.textingHoursStart ??
            assignment.subaccount.textingHoursStart
          }
          textingHoursEnd={
            assignment.campaign?.textingHoursEnd ??
            assignment.subaccount.textingHoursEnd
          }
          textingHoursTimezone={
            assignment.campaign?.textingHoursTimezone ??
            assignment.subaccount.textingHoursTimezone
          }
        />
        <Buttons
          isOptedOut={false}
          handleOptOut={handleOptOut}
          needsResponse={assignment.needsTexterResponse}
          handleMarkNeedsResponse={handleMarkNeedsResponse}
          handleMarkNotNeedsResponse={handleMarkNotNeedsResponse}
          handleEscalate={handleEscalate}
          handleDeescalate={
            inboxType === InboxType.Escalation ? handleDeescalate : null
          }
          inboxName={inboxName}
          mutationPending={mutationPending}
          ownedPhoneNumber={assignment.fromOwnedPhoneNumber}
        />
      </Box>
      <StyledTabs
        value={
          tabsOpen && assignment.campaignContact
            ? TABS.indexOf(activeTab)
            : false
        }
        onChange={(evt, val) => handleSelectTab(TABS[val])}
        variant="fullWidth"
      >
        <StyledTab
          label={
            <FormattedMessage
              defaultMessage="Responses"
              description="Tab header for canned responses in the texter view"
            />
          }
        />
        <StyledTab
          label={
            <>
              {unsavedTags.length > 0 && (
                <NeedsResponseBullet
                  sx={{ width: "8px", marginRight: "4px" }}
                />
              )}
              <FormattedMessage
                defaultMessage="Tags"
                description="Tab header for tags in the texter view"
              />
            </>
          }
        />
        <StyledTab
          label={
            <>
              {unsavedContactNote != null && (
                <NeedsResponseBullet
                  sx={{ width: "8px", marginRight: "4px" }}
                />
              )}
              <FormattedMessage
                defaultMessage="Contact Details"
                description="Tab header for contact details in the texter view"
              />
            </>
          }
        />
        <StyledTab
          label={tabsOpen ? <ExpandMore /> : <ExpandLess />}
          sx={{ minWidth: "28px", maxWidth: "28px", width: "28px" }}
        />
      </StyledTabs>
      <Collapse in={tabsOpen}>
        {tabsOpen &&
          (assignment.campaignContact ? (
            <Box
              sx={{
                height: "280px",
                overflowY: "auto",
                backgroundColor: palette.lightestGray,
                padding: "24px 40px",
              }}
            >
              {" "}
              {renderActiveTab()}
            </Box>
          ) : (
            <FlexBox
              sx={{ px: 5, py: 3, fontSize: "12px", color: palette.darkGray }}
              justifyContent="center"
            >
              <FormattedMessage
                defaultMessage="This contact does not currently belong to a campaign."
                description="Message for contact pane when thread has no owning campaign"
              />
            </FlexBox>
          ))}
      </Collapse>
    </>
  );
}
